import GTranslateIcon from '@mui/icons-material/GTranslate';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Rating from '@mui/material/Rating';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import React, { memo, useRef } from 'react';
import { useIntl } from 'react-intl';
import useIsInView from '../../util/hooks/useIsInView';

import useScreenSize from '../../util/hooks/useScreenSize';
import ListingLink from '../Links/ListingLink/ListingLink';
import NamedLink from '../Links/NamedLink/NamedLink';
import UserDisplayName from '../User/UserDisplayName/UserDisplayName';

/**
 * Displays an individual review with author details and content.
 */
const Review = memo((props) => {
  const intl = useIntl();
  const { review, getTranslation } = props;

  const date = review.attributes.createdAt;
  const dateString = intl.formatDate(date, { month: 'long', year: 'numeric' });

  const { translations, defaultLocale } = getTranslation(review.id.uuid, 'review', intl.locale);
  const isTranslated = !!defaultLocale && defaultLocale !== intl.locale;
  const content = translations.content || review.attributes.content;

  const hasProfileImage = review.author.profileImage && review.author.profileImage.id;
  const reviewAuthorProfileAvatarUrl = hasProfileImage
    ? review.author.profileImage.attributes?.variants['square-small']?.url
    : null;

  const avatarRef = useRef(null);
  const isInViewPort = useIsInView(avatarRef, { threshold: 0.7 }, true);

  const { isDesktop } = useScreenSize();

  // The author name links to the author's profile (guest or host), and the booked
  // listing is shown as a separate link to its ListingPage — so hosts can both look
  // up who reviewed and discover the places involved.
  const author = review.author;
  const authorId = author?.id?.uuid;
  const authorIsUnavailable = author?.attributes?.deleted || author?.attributes?.banned;
  const authorName = <UserDisplayName user={author} />;
  const authorElement =
    authorId && !authorIsUnavailable ? (
      <NamedLink name="ProfilePage" params={{ id: authorId }}>
        {authorName}
      </NamedLink>
    ) : (
      authorName
    );

  const listing = review.listing;
  const hasListing = listing && listing.id;

  return (
    <Grid size={{ lg: 12, md: 6, xs: 12 }}>
      <Stack spacing={2}>
        <Box
          alignItems={isDesktop ? 'flex-start' : 'center'}
          display="flex"
          flexDirection={isDesktop ? 'row' : 'column'}
          ref={avatarRef}
        >
          {isInViewPort ? (
            <Avatar
              src={reviewAuthorProfileAvatarUrl}
              sx={{
                width: 100,
                height: 100,
                marginRight: isDesktop ? 2 : 0,
                marginBottom: isDesktop ? 0 : 1,
              }}
            />
          ) : null}
          <Box textAlign={isDesktop ? 'left' : 'center'}>
            {authorElement}
            {hasListing ? (
              <Typography variant="body2">
                <ListingLink listing={listing} />
              </Typography>
            ) : null}
            <Typography variant="body2">{dateString}</Typography>
            <Rating
              precision={0.1}
              readOnly
              sx={{ color: '#EAC435' }}
              value={review.attributes.rating}
            />
          </Box>
        </Box>
        <Typography variant="body1">
          {isTranslated ? (
            <Tooltip title={intl.formatMessage({ id: 'Review.isTranslation' })}>
              <GTranslateIcon fontSize="small" />
            </Tooltip>
          ) : null}
          {content}
        </Typography>
      </Stack>
    </Grid>
  );
});

export default Review;
